// Profil-media (IPTV TV + MP3) admin data layer (tz/mp3-tv FAZA 4). Self-contained:
// does NOT touch `lib/adapters.ts` / `i18n/*` (hot files). Backend `/admin/tv/*`
// + `/admin/music/*` (FAZA 1) — every fetcher is fail-soft (empty until deployed).
import { api } from '@/lib/api'
import type { FetchParams, FetchResult } from '@/components/DataTable/types'

function toResult<T>(rows: T[], params: FetchParams, search?: (r: T) => string): FetchResult<T> {
  const q = params.search.trim().toLowerCase()
  const filtered = q && search ? rows.filter((r) => search(r).toLowerCase().includes(q)) : rows
  const start = params.page * params.pageSize
  return { rows: filtered.slice(start, start + params.pageSize), total: filtered.length }
}

async function safeList<T>(path: string, key: string, signal: AbortSignal): Promise<T[]> {
  try {
    const data = await api.get<Record<string, T[]>>(path, { signal })
    return (data?.[key] as T[] | undefined) ?? []
  } catch {
    return []
  }
}

export interface IptvChannelRow {
  id: number
  name: string
  country: string
  category: string
  stream_url: string
  logo_url: string
  is_premium: boolean
  sort_order: number
  enabled: boolean
}

export interface SongRow {
  id: number
  title: string
  artist: string
  audio_url: string
  cover_url: string
  duration_sec: number
  genre: string
  is_premium: boolean
  status: string // approved|pending|rejected
  plays: number
  uploaded_by?: number
}

// ── TV ───────────────────────────────────────────────────────────────────────
export function fetchIptvChannels(params: FetchParams): Promise<FetchResult<IptvChannelRow>> {
  return safeList<IptvChannelRow>('/admin/tv/channels', 'channels', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.name} ${r.country} ${r.category}`),
  )
}

/** POST /admin/tv/import — pull the m-kino channel list (upsert). */
export function importChannels(url?: string): Promise<{ imported: number; updated: number }> {
  return api.post<{ imported: number; updated: number }>('/admin/tv/import', url ? { url } : {})
}

export function createChannel(body: Partial<IptvChannelRow>): Promise<{ id: number }> {
  return api.post<{ id: number }>('/admin/tv/channels', body)
}
export function updateChannel(id: number, body: Partial<IptvChannelRow>): Promise<unknown> {
  return api.put(`/admin/tv/channels/${id}`, body)
}
export function deleteChannel(id: number): Promise<unknown> {
  return api.del(`/admin/tv/channels/${id}`)
}

/** Countries whose channels are all disabled (fully blocked). Dormant → []. */
export async function fetchBlockedCountries(): Promise<string[]> {
  try {
    const d = await api.get<{ countries: string[] }>('/admin/tv/blocked-countries')
    return d?.countries ?? []
  } catch {
    return []
  }
}

/** Bulk block/unblock every channel of a country. */
export function blockCountry(country: string, blocked: boolean): Promise<{ affected: number }> {
  return api.post<{ affected: number }>('/admin/tv/block-country', { country: country.toUpperCase(), blocked })
}

// ── MP3 ──────────────────────────────────────────────────────────────────────
export function fetchSongs(params: FetchParams): Promise<FetchResult<SongRow>> {
  return safeList<SongRow>('/admin/music/songs', 'songs', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.artist}`),
  )
}

/** Pending user uploads awaiting moderation. */
export function fetchPendingSongs(params: FetchParams): Promise<FetchResult<SongRow>> {
  return safeList<SongRow>('/admin/music/songs?status=pending', 'songs', params.signal).then((rows) =>
    toResult(rows, params, (r) => `${r.title} ${r.artist}`),
  )
}

export function moderateSong(id: number, decision: 'approve' | 'reject'): Promise<unknown> {
  return api.post(`/admin/music/songs/${id}/moderate`, { decision })
}
export function setSongPremium(id: number, premium: boolean): Promise<unknown> {
  return api.post(`/admin/music/songs/${id}/premium`, { premium })
}
export function deleteSong(id: number): Promise<unknown> {
  return api.del(`/admin/music/songs/${id}`)
}
